import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserCircle, ToggleLeft, ToggleRight, CheckCircle, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useMBList } from '../hooks/useMBList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HandlerManagement = () => {
  const [handlers, setHandlers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHandlers();
  }, []);

  const loadHandlers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/handlers/list`);
      setHandlers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load handlers:', error);
      toast.error('Failed to load handlers');
      setLoading(false);
    }
  };

  const handleToggleActive = async (handlerId, currentStatus, handlerName) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'activate' : 'deactivate';
    
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${handlerName}? ${!newStatus ? 'They will not be able to login.' : ''}`)) {
      return;
    }

    try {
      const response = await axios.put(`${API}/handlers/toggle-active/${handlerId}`);
      toast.success(`${handlerName} ${newStatus ? 'activated' : 'deactivated'} successfully!`);
      loadHandlers(); // Reload list
    } catch (error) {
      console.error('Failed to toggle handler status:', error);
      toast.error('Failed to update handler status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-gray-500">Loading handlers...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-lg font-bold text-gray-800 mb-4">Handler Management</h2>
      <p className="text-sm text-gray-600 mb-6">
        Control handler access. Deactivated handlers cannot login.
      </p>

      {handlers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <UserCircle size={48} weight="light" className="mx-auto mb-3 text-gray-300" />
          <p>No handlers found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {handlers.map((handler) => {
            const isActive = handler.active !== false; // Default to true if not set
            
            return (
              <div
                key={handler.id}
                className={`p-4 border rounded-lg transition-colors ${
                  isActive ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                }`}
              >
                {/* Session-49: on mobile the top row shows avatar + summary
                    + Deactivate side-by-side, and permission blocks stack
                    below at FULL container width so long labels like
                    "Mobile Number — Full View (else masked)" render on one
                    line instead of wrapping across 5 rows inside a
                    squeezed column. */}
                <div className="flex items-start gap-3 sm:gap-4">
                  <UserCircle size={40} weight="fill" className="text-gray-400 flex-shrink-0" />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-800 truncate">{handler.username}</p>
                      {isActive ? (
                        <CheckCircle size={18} weight="fill" className="text-green-600 flex-shrink-0" />
                      ) : (
                        <XCircle size={18} weight="fill" className="text-red-600 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 truncate">{handler.phone}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        {handler.role.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleToggleActive(handler.id, isActive, handler.username)}
                    className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium flex-shrink-0 transition-colors ${
                      isActive
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }`}
                  >
                    {isActive ? (
                      <>
                        <ToggleRight size={20} weight="bold" />
                        <span className="text-sm hidden sm:inline">Deactivate</span>
                      </>
                    ) : (
                      <>
                        <ToggleLeft size={20} weight="bold" />
                        <span className="text-sm hidden sm:inline">Activate</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Full-width permission stacks — labels no longer clipped */}
                <div className="mt-3 space-y-2">
                  <HandlerMbAssignChips handler={handler} />
                  {handler.role === 'calling_handler' && <CallingPhoneMaskToggle handler={handler} />}
                  <HandlerPanelPermissions handler={handler} />
                  <HandlerSettingsPermissions handler={handler} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HandlerManagement;

// ==== Issue 9: Calling Handler client-phone masking — ADMIN-controlled ====
// A calling_handler-only toggle (kept out of SETTINGS_PERMISSION_ITEMS on
// purpose so other roles are unaffected). ON = full phone visible in the
// Client Directory; OFF = masked (last-4). Masking is VISUAL only —
// call/WhatsApp always dial the full number.
function CallingPhoneMaskToggle({ handler }) {
  const [showFull, setShowFull] = React.useState(!!handler.calling_handler_show_full_phone);
  const [saving, setSaving] = React.useState(false);
  const adminId = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('dws_user') || '{}').id; } catch { return null; }
  }, []);

  const toggle = async () => {
    if (!adminId) { toast.error('Not logged in as admin.'); return; }
    const next = !showFull;
    setSaving(true);
    setShowFull(next); // optimistic
    try {
      await axios.put(`${API}/calling-handler/${handler.id}/phone-display`, { admin_id: adminId, full: next });
      toast.success(next ? 'Full phone numbers enabled' : 'Phone numbers masked (last 4)');
    } catch (e) {
      setShowFull(!next); // revert
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="flex items-center justify-between gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`calling-phone-mask-${handler.id}`}>
      <div className="min-w-0">
        <div className="text-[12px] font-bold text-amber-800">Client Phone Numbers</div>
        <div className="text-[10px] text-amber-600">{showFull ? 'Full number visible' : 'Masked — last 4 digits only'}</div>
      </div>
      <button
        onClick={toggle}
        disabled={saving}
        role="switch"
        aria-checked={showFull}
        data-testid={`calling-phone-mask-toggle-${handler.id}`}
        className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 ${showFull ? 'bg-emerald-600' : 'bg-gray-300'}`}
      >
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${showFull ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}

// ==== Feature 2: MB Assignment chips per handler ====
// Point 48 — chat types a Calling Handler can optionally be restricted to
// viewing (read-only) for its assigned MBs. Backend field: chat_types.
const CALLING_CHAT_TYPES = [
  { key: 'deposit', label: 'Deposit' },
  { key: 'withdrawal', label: 'Withdrawal' },
  { key: 'support', label: 'Support' },
  { key: 'inquiry', label: 'Inquiry' },
];

function HandlerMbAssignChips({ handler }) {
  const MB_LIST = useMBList();
  const [assigned, setAssigned] = React.useState(handler.assigned_mbs || []);
  // Point 48 — only meaningful for role === 'calling_handler'; empty/all
  // unchecked = every chat type viewable (mirrors the "All" MB semantics).
  const [chatTypes, setChatTypes] = React.useState(handler.calling_handler_chat_types || []);
  const [saving, setSaving] = React.useState(false);
  const adminId = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('dws_user') || '{}').id; } catch { return null; }
  }, []);
  const isAll = assigned.length === 0;
  const isCallingHandler = handler.role === 'calling_handler';
  const chatTypesAll = chatTypes.length === 0;

  const persist = async (nextMbs, nextChatTypes) => {
    if (!adminId) {
      toast.error('Not logged in as admin. Please re-login.');
      return;
    }
    setSaving(true);
    try {
      const body = { admin_id: adminId, mbs: nextMbs };
      // Only send chat_types for calling handlers — other roles don't use
      // this field and the backend simply ignores it if sent regardless.
      if (isCallingHandler) body.chat_types = nextChatTypes;
      const res = await axios.put(`${API}/users/${handler.id}/assigned-mbs`, body);
      setAssigned(res.data?.assigned_mbs || nextMbs);
      if (isCallingHandler) setChatTypes(res.data?.calling_handler_chat_types || nextChatTypes);
      toast.success(nextMbs.length === 0 ? 'Access set to All MBs' : `Access updated: ${nextMbs.join(', ')}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Update failed';
      console.error('MB assign failed', e, 'admin_id:', adminId);
      toast.error(`MB assign failed: ${detail}`);
    } finally { setSaving(false); }
  };

  return (
    <div data-testid={`mb-assign-${handler.id}`}>
      <div className="mt-2 flex flex-wrap gap-1 items-center">
        <span className="text-[10px] font-bold text-gray-500 uppercase mr-1">MBs:</span>
        <button
          onClick={() => persist([], chatTypes)}
          disabled={saving}
          data-testid={`mb-assign-all-${handler.id}`}
          className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${
            isAll ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'
          }`}
        >All</button>
        {MB_LIST.map(mb => {
          const on = !isAll && assigned.includes(mb);
          return (
            <button
              key={mb}
              onClick={() => {
                const next = on ? assigned.filter(x => x !== mb) : [...assigned.filter(x => x !== mb), mb];
                persist(next, chatTypes);
              }}
              disabled={saving}
              data-testid={`mb-assign-${mb}-${handler.id}`}
              className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${
                on ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-300'
              }`}
            >{mb}</button>
          );
        })}
        {saving && <span className="text-[10px] text-gray-400 ml-1">saving…</span>}
      </div>

      {/* Point 48 — Calling Handler only: which chat types are viewable
          (read-only) within the assigned MBs. Any combo or "All". */}
      {isCallingHandler && (
        <div className="mt-1.5 flex flex-wrap gap-1 items-center" data-testid={`calling-chat-types-${handler.id}`}>
          <span className="text-[10px] font-bold text-gray-500 uppercase mr-1">Viewable Chats:</span>
          <button
            onClick={() => persist(assigned, [])}
            disabled={saving}
            data-testid={`calling-chat-type-all-${handler.id}`}
            className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${
              chatTypesAll ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >All</button>
          {CALLING_CHAT_TYPES.map(({ key, label }) => {
            const on = !chatTypesAll && chatTypes.includes(key);
            return (
              <button
                key={key}
                onClick={() => {
                  const next = on ? chatTypes.filter(x => x !== key) : [...chatTypes.filter(x => x !== key), key];
                  persist(assigned, next);
                }}
                disabled={saving}
                data-testid={`calling-chat-type-${key}-${handler.id}`}
                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${
                  on ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-300'
                }`}
              >{label}</button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==== Panel Permissions per-handler (History / Summary / Excel / PDF) ====
const PANEL_KEYS = [
  { key: 'history', label: 'History & Filter' },
  { key: 'summary', label: 'Summary' },
  { key: 'excel',   label: 'Excel Report' },
  { key: 'pdf',     label: 'PDF Report' },
];
const SCOPE_OPTIONS = [
  { key: 'both',     label: 'Both' },
  { key: 'withdraw', label: 'Withdraw' },
  { key: 'deposit',  label: 'Deposit' },
];
const DATE_PRESETS = [
  { key: 'all',    label: 'All time' },
  { key: '2d',     label: 'Last 2 days' },
  { key: '7d',     label: 'Last 7 days' },
  { key: '30d',    label: 'Last 1 month' },
  { key: 'custom', label: 'Custom' },
];

function HandlerPanelPermissions({ handler }) {
  const initial = React.useMemo(() => {
    const p = handler.panel_permissions || {};
    const out = {};
    PANEL_KEYS.forEach(({ key }) => {
      const v = p[key] || {};
      const dr = v.date_range || {};
      out[key] = {
        visible: v.visible !== false,
        scope: SCOPE_OPTIONS.find(s => s.key === v.scope) ? v.scope : 'both',
        download: v.download !== false,
        date_range: {
          preset: DATE_PRESETS.find(d => d.key === dr.preset) ? dr.preset : 'all',
          from: dr.from || '',
          to: dr.to || '',
        },
      };
    });
    return out;
  }, [handler.panel_permissions]);
  const [perms, setPerms] = React.useState(initial);
  const [expanded, setExpanded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const adminId = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('dws_user') || '{}').id; } catch { return null; }
  }, []);

  const persist = async (next) => {
    if (!adminId) { toast.error('Not logged in as admin.'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/users/${handler.id}/panel-permissions`,
        { admin_id: adminId, permissions: next });
      setPerms((prev) => ({ ...prev, ...(res.data?.panel_permissions || next) }));
      toast.success('Panel permissions saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const patch = (key, changes) => {
    const next = { ...perms, [key]: { ...perms[key], ...changes } };
    setPerms(next);
    persist(next);
  };
  // Nested patch for date_range without spamming saves until commit is meaningful
  const patchDate = (key, drChanges, commit = true) => {
    const next = {
      ...perms,
      [key]: { ...perms[key], date_range: { ...perms[key].date_range, ...drChanges } },
    };
    setPerms(next);
    if (commit) persist(next);
  };

  return (
    <div className="mt-2" data-testid={`panel-perm-${handler.id}`}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="text-[10px] font-bold uppercase text-slate-600 hover:text-slate-900 flex items-center gap-1"
      >
        <span>📋 Panel Access</span>
        <span className="text-slate-400">{expanded ? '▴' : '▾'}</span>
        {saving && <span className="text-slate-400 normal-case">saving…</span>}
      </button>
      {expanded && (
        <div className="mt-1.5 grid grid-cols-1 gap-2 bg-slate-50 border border-slate-200 rounded-md p-2">
          {PANEL_KEYS.map(({ key, label }) => {
            const p = perms[key] || { visible: true, scope: 'both', download: true, date_range: { preset: 'all', from: '', to: '' } };
            const dr = p.date_range || { preset: 'all', from: '', to: '' };
            return (
              <div key={key} className="border-b border-slate-200 last:border-b-0 pb-2 last:pb-0">
                <div className="flex items-center justify-between gap-2 text-[11px] flex-wrap">
                  <label className="flex items-center gap-1.5 cursor-pointer min-w-[100px]">
                    <input
                      type="checkbox"
                      checked={p.visible}
                      onChange={(e) => patch(key, { visible: e.target.checked })}
                      className="w-3.5 h-3.5"
                      data-testid={`panel-perm-${handler.id}-${key}-visible`}
                    />
                    <span className={p.visible ? 'font-semibold text-slate-800' : 'text-slate-400 line-through'}>
                      {label}
                    </span>
                  </label>
                  <div className="flex items-center gap-1 flex-wrap">
                    {SCOPE_OPTIONS.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => patch(key, { scope: s.key })}
                        disabled={!p.visible}
                        className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold border ${p.scope === s.key ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300'} ${!p.visible ? 'opacity-40 cursor-not-allowed' : ''}`}
                        data-testid={`panel-perm-${handler.id}-${key}-scope-${s.key}`}
                      >
                        {s.label}
                      </button>
                    ))}
                    {key === 'pdf' && (
                      <label className="flex items-center gap-1 ml-1 cursor-pointer" title="Allow downloading the PDF">
                        <input
                          type="checkbox"
                          checked={p.download}
                          onChange={(e) => patch(key, { download: e.target.checked })}
                          disabled={!p.visible}
                          className="w-3 h-3"
                          data-testid={`panel-perm-${handler.id}-pdf-download`}
                        />
                        <span className="text-[9px] font-semibold text-slate-600">Download</span>
                      </label>
                    )}
                  </div>
                </div>
                {/* Date-range picker — controls how far back this handler can
                    view/export data for THIS module. */}
                <div className="flex items-center gap-2 mt-1 pl-5 flex-wrap">
                  <span className="text-[9px] uppercase tracking-wide font-semibold text-slate-500">Date</span>
                  <select
                    value={dr.preset}
                    onChange={(e) => patchDate(key, {
                      preset: e.target.value,
                      // Reset custom fields when leaving custom
                      from: e.target.value === 'custom' ? dr.from : '',
                      to:   e.target.value === 'custom' ? dr.to   : '',
                    })}
                    disabled={!p.visible}
                    className={`text-[10px] rounded border border-slate-300 bg-white px-1.5 py-0.5 ${!p.visible ? 'opacity-40 cursor-not-allowed' : ''}`}
                    data-testid={`panel-perm-${handler.id}-${key}-date-preset`}
                  >
                    {DATE_PRESETS.map((d) => (
                      <option key={d.key} value={d.key}>{d.label}</option>
                    ))}
                  </select>
                  {dr.preset === 'custom' && (
                    <div className="flex items-center gap-1">
                      <input
                        type="date"
                        value={dr.from || ''}
                        onChange={(e) => patchDate(key, { from: e.target.value }, false)}
                        onBlur={() => persist(perms)}
                        disabled={!p.visible}
                        className="text-[10px] rounded border border-slate-300 bg-white px-1 py-0.5"
                        data-testid={`panel-perm-${handler.id}-${key}-date-from`}
                      />
                      <span className="text-slate-400">→</span>
                      <input
                        type="date"
                        value={dr.to || ''}
                        onChange={(e) => patchDate(key, { to: e.target.value }, false)}
                        onBlur={() => persist(perms)}
                        disabled={!p.visible}
                        className="text-[10px] rounded border border-slate-300 bg-white px-1 py-0.5"
                        data-testid={`panel-perm-${handler.id}-${key}-date-to`}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================
// Session-35 — Settings power grants
// ============================================
const SETTINGS_PERMISSION_ITEMS = [
  { key: 'client_list',       label: 'Client List (→ Client Management)' },
  { key: 'client_directory',  label: 'Client Directory' },
  { key: 'create_client',     label: 'Create Client' },
  { key: 'appearance',        label: 'Appearance' },
  { key: 'broadcast',         label: 'Broadcast' },
  { key: 'status',            label: 'Status' },
  { key: 'reports',           label: 'Reports (Excel / Summary / PDF)' },
  { key: 'service_timeline',  label: 'Service Timelines (Requests)' },
  // Session-48h — new privacy toggle. When OFF, phone numbers show masked
  // (XXXXXX + last-4) in every list/table view. Handler can still see the
  // full number by opening the customer profile / Edit modal.
  { key: 'mobile_view_full',  label: 'Mobile Number — Full View (else masked)' },
  // Session-48h — enquiry handler quick powers (only relevant for
  // inquiry_handler role but stored in the same permissions blob).
  { key: 'enquiry_assign_mb', label: 'Enquiry Handler — Assign to MB' },
];

function HandlerSettingsPermissions({ handler }) {
  const { user } = useAuth();
  const [expanded, setExpanded] = React.useState(false);
  const [perms, setPerms] = React.useState(null);
  const [busyKey, setBusyKey] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/users/handler/${handler.id}/permissions`);
      setPerms(r.data?.permissions || {});
    } catch (_) { /* silent */ }
  }, [handler.id]);

  React.useEffect(() => { if (expanded && !perms) load(); }, [expanded, perms, load]);

  const toggle = async (key) => {
    const next = { ...(perms || {}), [key]: !((perms || {})[key]) };
    setBusyKey(key);
    setPerms(next); // optimistic
    try {
      await axios.put(`${API}/users/handler/${handler.id}/permissions`, {
        actor_id: user?.id,
        permissions: { [key]: next[key] },
      });
      toast.success(`${key.replace(/_/g, ' ')} ${next[key] ? 'granted' : 'revoked'}`);
    } catch (e) {
      setPerms(perms); // revert
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setBusyKey(null); }
  };

  const grantedCount = Object.values(perms || {}).filter(Boolean).length;

  return (
    <div className="mt-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-[11px] font-bold text-indigo-700 hover:text-indigo-900 flex items-center gap-1 px-2 py-1 rounded border border-indigo-200 hover:bg-indigo-50"
        data-testid={`handler-settings-perms-toggle-${handler.id}`}
      >
        🔑 Settings Powers {perms && <span className="text-indigo-500 font-normal">({grantedCount}/{SETTINGS_PERMISSION_ITEMS.length})</span>}
        <span className="text-[9px]">{expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && (
        <div className="mt-2 p-2.5 bg-indigo-50 rounded-lg border border-indigo-200" data-testid={`handler-settings-perms-panel-${handler.id}`}>
          <div className="text-[10px] text-indigo-600 mb-2 font-semibold uppercase tracking-wide">Grant / Revoke Settings Access</div>
          {/* Session-39: single-column stacked list. Labels always visible on
              both mobile + web (grid-cols-2 was clipping to icons only on
              narrow screens). Bigger tap targets + clear ON/OFF states. */}
          <div className="flex flex-col gap-1.5">
            {SETTINGS_PERMISSION_ITEMS.map((it) => {
              const on = !!(perms || {})[it.key];
              const busy = busyKey === it.key;
              return (
                <button
                  key={it.key}
                  onClick={() => toggle(it.key)}
                  disabled={busy || !perms}
                  className={`w-full text-[12px] font-semibold px-3 py-2.5 rounded-md border transition-all text-left flex items-center gap-2.5 ${
                    on
                      ? 'bg-emerald-500 text-white border-emerald-600 shadow-sm hover:bg-emerald-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                  } ${busy ? 'opacity-60 cursor-wait' : ''} disabled:opacity-50`}
                  data-testid={`handler-settings-perm-${handler.id}-${it.key}`}
                  aria-pressed={on}
                >
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded ${
                    on ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-400 border border-slate-300'
                  } text-[13px]`}>
                    {busy ? '…' : (on ? '✓' : '')}
                  </span>
                  <span className="flex-1">{it.label}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    on ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-500'
                  }`}>
                    {on ? 'ON' : 'OFF'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
